import { ComponentType } from 'react';

import dynamic from 'next/dynamic';

import { defaultLocale, locales } from 'libs/locale';

import { Alternative } from 'stores/intl/ints.types';

import { hasLocalizedSlug, keyForLocalizedSlug, localizedSlug } from './localizedSlugs';
import { StaticPageLoading } from './StaticPageLoading';

export interface StaticPageEntry {
  title: string;
  description: string;
  /**
   * Optional translation dictionary key. When set, its JSON file at
   * pages/api/internationalization/data/<key>.json is fetched per-request
   * and made available to the page's sections via useInt()'s t(), exactly
   * like CMS-driven pages. Start the file as `{}` and fill it in later —
   * missing keys/files fall back to the English copy already in the JSX
   * (t() returns its argument unchanged when no translation is found), so a
   * page works identically before and after translations are added.
   * Naming convention already used by the (currently empty, CMS-prepared)
   * files under that folder: `<domain>_<page_name>`, e.g.
   * `platform_corporate_linkage`, `solutions_master_data_management`.
   */
  internationalizationKey?: string;
  Component: ComponentType;
}

/**
 * ============================================================================
 *  STATIC PAGES — how this whole mechanism works, start to finish
 * ============================================================================
 *
 * A "static page" is a marketing/landing page whose content is hardcoded in
 * React — no CMS "page" record behind it. This file is the ONLY list of
 * which slugs are static pages; everything else reads from it.
 *
 * ── The 4 pieces, and when you'd ever open each one ──────────────────────
 *
 * 1. THIS FILE (registry.tsx) — the map of slug → { title, description,
 *    Component }. You touch this constantly: every add/remove goes through
 *    here. Nothing else.
 *
 * 2. Each page's own folder, src/landing-pages/<slug>/ — the actual
 *    page component + its own sections/ subfolder + its own .module.scss.
 *    Fully self-contained: deleting the whole folder is always safe once its
 *    entry below is also removed.
 *
 * 3. pages/[...route].tsx — the site's one catch-all route. On every
 *    request it checks: is this slug in `staticPages` (below)? If yes, it
 *    renders StaticPageRoute (#4). If no, it falls through to the normal
 *    CMS flow (ssr/getServerSideProps.tsx → templates.tsx). You will
 *    essentially never need to touch this file.
 *
 * 4. StaticPageRoute.tsx (next to this file) — the header/footer/providers
 *    shell (equivalent of the CMS flow's Page.tsx, minus the CMS fetch),
 *    which looks the slug up here and renders its Component inside. You
 *    will essentially never need to touch this file either.
 *
 * ── To add a page ──────────────────────────────────────────────────────────
 * Build src/landing-pages/<new-slug>/ (component + sections/ +
 * .module.scss, same shape as any entry below), then add one entry to the
 * object below. That's it — no file anywhere else changes.
 *
 * ── To remove a page ────────────────────────────────────────────────────────
 * Delete src/landing-pages/<slug>/, delete its entry below. Done.
 *
 * ── Why next/dynamic everywhere ────────────────────────────────────────────
 * Wrapping each Component in next/dynamic keeps every page its own
 * lazily-loaded chunk — visiting one static page never downloads another's
 * code, same as if each still had its own pages/<slug>.tsx file.
 */
export const staticPages: Record<string, StaticPageEntry> = {
  // Solutions — Risk & Compliance
  'kyb-verification': {
    title: 'KYB Verification Against 400 Government Registries',
    description:
      'Verify any business against official registries in 200+ countries. Status, ownership and filings, with the source and timestamp on every field.',
    internationalizationKey: 'platform_business_verification_kyb',
    Component: dynamic(
      () =>
        import('landing-pages/kyb-verification/BusinessVerificationKybPage').then((m) => m.BusinessVerificationKybPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'ubo-discovery': {
    title: 'UBO Discovery: Beneficial Owners in 200+ Countries',
    description:
      'Resolve ultimate beneficial owners, shareholders and directors from registry filings. Ownership percentages and full group structure included.',
    internationalizationKey: 'platform_ubo_discovery',
    Component: dynamic(() => import('landing-pages/ubo-discovery/UboDiscoveryPage').then((m) => m.UboDiscoveryPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  'corporate-linkage': {
    title: 'Corporate Linkage: Map Any Group Structure',
    description:
      'Parents, subsidiaries and sister entities resolved into one group tree, built from filings across 400+ government registries in 200+ countries.',
    internationalizationKey: 'platform_corporate_linkage',
    Component: dynamic(
      () => import('landing-pages/corporate-linkage/CorporateLinkagePage').then((m) => m.CorporateLinkagePage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'peps-sanctions': {
    title: 'PEP & Sanctions Screening: OFAC, EU, UN, UK HMT',
    description:
      'Screen companies, directors and beneficial owners against OFAC, EU, UN and UK HMT lists, in the same call that verifies the legal entity.',
    internationalizationKey: 'platform_peps_sanctions',
    Component: dynamic(
      () => import('landing-pages/peps-sanctions/PepsSanctionsPage').then((m) => m.PepsSanctionsPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  // Solutions — Credit & Financial Risk
  'company-financials': {
    title: 'Company Financials API: 20 Years of Filed Accounts',
    description:
      'Filed accounts and modelled figures for private and public companies, up to 20 years of history, each linked to the original registry filing.',
    internationalizationKey: 'platform_company_financials',
    Component: dynamic(
      () => import('landing-pages/company-financials/CompanyFinancialsPage').then((m) => m.CompanyFinancialsPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'business-credit-risk': {
    title: 'Business Credit Risk Scores From Filed Accounts',
    description:
      'Assess counterparty credit risk with filed accounts, registry status and payment signals, scored consistently across 200+ countries.',
    internationalizationKey: 'platform_business_credit_risk',
    Component: dynamic(
      () => import('landing-pages/business-credit-risk/BusinessCreditRiskPage').then((m) => m.BusinessCreditRiskPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'bank-account-verification': {
    title: 'Bank Account Verification API: Payee and Company',
    description:
      'Validate the receiving account through payment schemes, then verify the company and its owners against official registries. 49 markets, 187ms.',
    internationalizationKey: 'platform_bank_account_verification',
    Component: dynamic(
      () =>
        import('landing-pages/bank-account-verification/BankAccountVerificationPage').then(
          (m) => m.BankAccountVerificationPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'continuous-monitoring': {
    title: 'Continuous Company Monitoring and Registry Alerts',
    description:
      "Be alerted when a company's status, directors, ownership or filings change at the registry. Delivered by webhook, API or email.",
    internationalizationKey: 'platform_continuous_monitoring',
    Component: dynamic(
      () =>
        import('landing-pages/continuous-monitoring/ContinuousMonitoringPage').then((m) => m.ContinuousMonitoringPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  // Solutions — Go-to-Market
  prospecting: {
    title: 'B2B Prospecting Data: 600M+ Verified Companies',
    description:
      'Build target lists from filed revenue, headcount, industry and location across 600M+ companies, sourced from registries rather than scraped.',
    internationalizationKey: 'platform_prospecting',
    Component: dynamic(() => import('landing-pages/prospecting/ProspectingPage').then((m) => m.ProspectingPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  'ma-activity': {
    title: 'M&A Activity Data: Ownership Changes as They File',
    description:
      'Spot acquisitions, disposals and ownership changes the moment they appear in a government registry, across 200+ countries.',
    internationalizationKey: 'platform_ma_activity',
    Component: dynamic(() => import('landing-pages/ma-activity/MaActivityPage').then((m) => m.MaActivityPage), {
      loading: () => <StaticPageLoading />,
    }),
  },

  // Our Data — Data Delivery
  platform: {
    title: 'Company Intelligence Platform: Search, Verify, Monitor',
    description:
      'Search 600M+ companies, run KYB checks, pull financials and monitor a portfolio, in one web platform. No integration required.',
    internationalizationKey: 'platform_platform',
    Component: dynamic(
      () => import('landing-pages/platform/PlatformOverviewPage').then((m) => m.PlatformOverviewPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'regis-api': {
    title: 'Company Data API: Verify, Enrich and Screen Entities',
    description:
      'One REST API for company search, KYB verification, UBOs, financials and sanctions screening. Free sandbox key, no sales call required.',
    internationalizationKey: 'platform_regis_api',
    Component: dynamic(() => import('landing-pages/regis-api/RegisApiPage').then((m) => m.RegisApiPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  'browser-extension': {
    title: 'Company Data Browser Extension for Chrome',
    description:
      'See registry-verified company data, financials and beneficial owners on any website you visit, without leaving the page.',
    internationalizationKey: 'platform_browser_extension',
    Component: dynamic(
      () => import('landing-pages/browser-extension/BrowserExtensionPage').then((m) => m.BrowserExtensionPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'master-data-bulk-feed': {
    title: 'Company Master Data Bulk Feed: License the Full File',
    description:
      'License the complete company file or a filtered slice, delivered on a schedule as a bulk feed with source attribution on every record.',
    internationalizationKey: 'platform_master_data_bulk_feed',
    Component: dynamic(
      () => import('landing-pages/master-data-bulk-feed/MasterDataManagementPage').then((m) => m.default),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  // Who we serve — Industries
  'financial-services': {
    title: 'Company and UBO Data for Financial Services',
    description:
      'KYB, beneficial ownership, sanctions screening and filed financials for onboarding, credit and compliance teams in financial services.',
    internationalizationKey: 'platform_financial_services',
    Component: dynamic(
      () => import('landing-pages/financial-services/FinancialServicesPage').then((m) => m.FinancialServicesPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  banking: {
    title: 'KYB, UBO and Payee Verification for Banks',
    description:
      'Verify corporate customers and payees against 400+ government registries. Ownership, sanctions and continuous monitoring in one source.',
    internationalizationKey: 'platform_banking',
    Component: dynamic(() => import('landing-pages/banking/BankingPage').then((m) => m.BankingPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  insurance: {
    title: 'Company Data for Insurers: Underwriting and KYB',
    description:
      'Verify insured entities, map group structures and pull filed accounts for underwriting, claims and broker due diligence.',
    internationalizationKey: 'platform_insurance',
    Component: dynamic(() => import('landing-pages/insurance/InsurancePage').then((m) => m.InsurancePage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  technology: {
    title: 'Company Data for Technology and SaaS Platforms',
    description:
      'Embed KYB checks, company enrichment and ownership data in your product, through one API with 600M+ verified companies behind it.',
    internationalizationKey: 'platform_technology',
    Component: dynamic(() => import('landing-pages/technology/TechnologyPage').then((m) => m.TechnologyPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  fintech: {
    title: 'KYB and UBO Data for Fintechs: One API Call',
    description:
      'Onboard business customers in minutes. Registry verification, beneficial owners and sanctions screening from a single endpoint.',
    internationalizationKey: 'platform_fintech',
    Component: dynamic(() => import('landing-pages/fintech/FintechPage').then((m) => m.FintechPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  government: {
    title: 'Company Registry Data for Government and Public Sector',
    description:
      'Cross-border company, ownership and sanctions data from 400+ official registries, with full provenance for evidentiary use.',
    internationalizationKey: 'platform_government',
    Component: dynamic(() => import('landing-pages/government/GovernmentPage').then((m) => m.GovernmentPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  'defence-security': {
    title: 'Company Ownership Data for Defence and Security',
    description:
      'Trace ownership, control and corporate networks across 200+ jurisdictions, including secrecy-jurisdiction links and officer networks.',
    internationalizationKey: 'platform_defence_security',
    Component: dynamic(
      () => import('landing-pages/defence-security/DefenceSecurityPage').then((m) => m.DefenceSecurityPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  legal: {
    title: 'Company and Ownership Data for Law Firms',
    description:
      'Registry-sourced entity, officer and ownership records for due diligence, litigation support and conflict checks, with cited filings.',
    internationalizationKey: 'platform_legal',
    Component: dynamic(() => import('landing-pages/legal/LegalPage').then((m) => m.LegalPage), {
      loading: () => <StaticPageLoading />,
    }),
  },
  manufacturing: {
    title: 'Supplier Verification and Company Data for Manufacturers',
    description:
      'Verify suppliers before onboarding and monitor them after. Registry status, ownership, filed accounts and sanctions in one check.',
    internationalizationKey: 'platform_manufacturing',
    Component: dynamic(() => import('landing-pages/manufacturing/ManufacturingPage').then((m) => m.ManufacturingPage), {
      loading: () => <StaticPageLoading />,
    }),
  },

  // Who we serve — Use Cases
  'kyb-registry-verification': {
    title: 'KYB Registry Verification: Check Against the Source',
    description:
      'Match a business to its official registry record and return status, address, activities and filings, with the registry named on every field.',
    internationalizationKey: 'platform_kyb_registry_verification',
    Component: dynamic(
      () =>
        import('landing-pages/kyb-registry-verification/KybRegistryVerificationPage').then(
          (m) => m.KybRegistryVerificationPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'financial-due-diligence': {
    title: 'Financial Due Diligence Data: Accounts and Ownership',
    description:
      'Twenty years of filed accounts, group structure and beneficial ownership for any counterparty, pulled straight from official registries.',
    internationalizationKey: 'platform_financial_due_diligence',
    Component: dynamic(
      () =>
        import('landing-pages/financial-due-diligence/FinancialDueDiligencePage').then(
          (m) => m.FinancialDueDiligencePage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'supply-chain-third-party-risk': {
    title: 'Third-Party and Supply Chain Risk: Verify Every Vendor',
    description:
      'Screen and monitor suppliers across 200+ countries. Ownership, sanctions, financial health and registry status, refreshed continuously.',
    internationalizationKey: 'platform_supply_chain_third_party_risk',
    Component: dynamic(
      () =>
        import('landing-pages/supply-chain-third-party-risk/SupplyChainThirdPartyRiskPage').then(
          (m) => m.SupplyChainThirdPartyRiskPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'marketplace-seller-verification': {
    title: 'Marketplace Seller Verification at Onboarding',
    description:
      'Verify business sellers against official registries before payouts. Entity status, beneficial owners and sanctions in one API call.',
    internationalizationKey: 'platform_marketplace_seller_verification',
    Component: dynamic(
      () =>
        import('landing-pages/marketplace-seller-verification/MarketplaceSellerVerificationPage').then(
          (m) => m.MarketplaceSellerVerificationPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'investment-screening': {
    title: 'Investment Screening: Ownership and Financial Checks',
    description:
      'Screen targets and portfolio companies for ownership risk, sanctions exposure and financial deterioration, with sourced filings.',
    internationalizationKey: 'platform_investment_screening',
    Component: dynamic(
      () => import('landing-pages/investment-screening/InvestmentScreeningPage').then((m) => m.InvestmentScreeningPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'master-data-management': {
    title: 'Company Master Data Management With Registry Records',
    description:
      'Deduplicate, match and keep your entity records current against 400+ official registries, with a stable identifier for every company.',
    internationalizationKey: 'solutions_master_data_management',
    Component: dynamic(
      () =>
        import('landing-pages/master-data-management/MasterDataManagementPage').then((m) => m.MasterDataManagementPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'data-licensing-redistribution': {
    title: 'Company Data Licensing and Redistribution Rights',
    description:
      'License registry-sourced company data for use inside your own product, with redistribution rights and documented provenance.',
    internationalizationKey: 'platform_data_licensing_redistribution',
    Component: dynamic(
      () =>
        import('landing-pages/data-licensing-redistribution/DataLicensingRedistributionPage').then(
          (m) => m.DataLicensingRedistributionPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'crm-data-enrichment': {
    title: 'CRM Data Enrichment: Salesforce, HubSpot, Dynamics',
    description:
      'Fill and refresh company records in your CRM with registry-verified firmographics, financials and identifiers. Native integrations.',
    internationalizationKey: 'platform_crm_data_enrichment',
    Component: dynamic(
      () => import('landing-pages/crm-data-enrichment/CrmDataEnrichmentPage').then((m) => m.CrmDataEnrichmentPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'sales-prospecting-lead-generation': {
    title: 'Sales Prospecting With Verified Company Data',
    description:
      'Find and qualify accounts on filed revenue, headcount and industry, then push them to your CRM. 600M+ companies, registry-sourced.',
    internationalizationKey: 'platform_sales_prospecting_lead_generation',
    Component: dynamic(
      () =>
        import('landing-pages/sales-prospecting-lead-generation/SalesProspectingLeadGenerationPage').then(
          (m) => m.SalesProspectingLeadGenerationPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'tax-id-vat-validation': {
    title: 'VAT and Tax ID Validation Across 200+ Countries',
    description:
      'Validate VAT, EIN and national tax identifiers, then confirm the entity behind them against the official company registry.',
    internationalizationKey: 'solutions_tax_id_vat_validation',
    Component: dynamic(
      () => import('landing-pages/tax-id-vat-validation/TaxIdVatValidationPage').then((m) => m.TaxIdVatValidationPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'transfer-pricing': {
    title: 'Transfer Pricing Benchmarking: Comparable Company Data',
    description:
      'Build comparable sets from filed accounts across 200+ countries, with financial history, activity codes and ownership on every entity.',
    internationalizationKey: 'platform_transfer_pricing',
    Component: dynamic(
      () => import('landing-pages/transfer-pricing/TransferPricingPage').then((m) => m.TransferPricingPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'company-data-for-ai-agents': {
    title: 'Company Data for AI Agents: MCP Server and API',
    description:
      'Give Claude, ChatGPT or your own agents live access to 600M+ verified companies through a native MCP server, with cited sources.',
    internationalizationKey: 'platform_company_data_for_ai_agents',
    Component: dynamic(
      () =>
        import('landing-pages/company-data-for-ai-agents/CompanyDataForAiAgentsPage').then(
          (m) => m.CompanyDataForAiAgentsPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  // Platform sub-pages (page_type: "platform")
  'data-dictionary': {
    title: 'Company Data Dictionary: Every Field We Return',
    description:
      'Every field in the Global Database company record, with its type, coverage by country and the registry it is sourced from.',
    internationalizationKey: 'platform_data_dictionary',
    Component: dynamic(
      () => import('landing-pages/data-dictionary/DataDictionaryPage').then((m) => m.DataDictionaryPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  'data-sources': {
    title: 'Our Data Sources: 400+ Government Registries',
    description:
      'Where every record comes from: 400+ official registries across 200+ countries, how often each is refreshed, and what it publishes.',
    internationalizationKey: 'platform_data_sources',
    Component: dynamic(() => import('landing-pages/data-sources/DataSourcesPage').then((m) => m.DataSourcesPage), {
      loading: () => <StaticPageLoading />,
    }),
  },

  // Pricing
  'pricing-packages': {
    title: 'Pricing: Pay Per Verification, Volume Rates at Scale',
    description:
      'Transparent per-verification pricing with volume tiers, plus API, bulk feed and platform packages. Free trial, no card required.',
    Component: dynamic(
      () => import('landing-pages/pricing-packages/PricingPackagesPage').then((m) => m.PricingPackagesPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },

  // Compare — Global Database vs alternatives (shared template: landing-pages/compare)
  'global-database-vs-dnb': {
    title: 'Global Database vs Dun & Bradstreet: Data Compared',
    description:
      'How the two compare on registry coverage, ownership depth, refresh frequency, provenance and price. Side by side, no marketing gloss.',
    internationalizationKey: 'platform_global_database_vs_dnb',
    Component: dynamic(
      () => import('landing-pages/compare/pages/GlobalDatabaseVsDnbPage').then((m) => m.GlobalDatabaseVsDnbPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'global-database-vs-moodys': {
    title: "Global Database vs Moody's Orbis (Bureau van Dijk): Company Data Compared [2026]",
    description:
      "Global Database vs Moody's Orbis (formerly Bureau van Dijk / BvD). Compare data sourcing, reseller rights, digitized financials, ownership graphs, PEPs & sanctions, and bank account verification.",
    internationalizationKey: 'platform_global_database_vs_moodys',
    Component: dynamic(
      () => import('landing-pages/compare/pages/GlobalDatabaseVsMoodysPage').then((m) => m.GlobalDatabaseVsMoodysPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'global-database-vs-sayari': {
    title: 'Global Database vs Sayari: Company Data Compared [2026]',
    description:
      'Global Database vs Sayari. Compare data sourcing, reseller rights, digitized financials, ownership graphs, transfer pricing, and M&A history.',
    internationalizationKey: 'platform_global_database_vs_sayari',
    Component: dynamic(
      () => import('landing-pages/compare/pages/GlobalDatabaseVsSayariPage').then((m) => m.GlobalDatabaseVsSayariPage),
      { loading: () => <StaticPageLoading /> },
    ),
  },
  'global-database-vs-opencorporates': {
    title: 'Global Database vs OpenCorporates: Company Data Compared [2026]',
    description:
      'Global Database vs OpenCorporates: 600M+ vs 220M+ companies, real registry freshness data, UBO ownership graphs, PEPs & sanctions screening, and AI-native API access compared.',
    internationalizationKey: 'platform_global_database_vs_opencorporates',
    Component: dynamic(
      () =>
        import('landing-pages/compare/pages/GlobalDatabaseVsOpenCorporatesPage').then(
          (m) => m.GlobalDatabaseVsOpenCorporatesPage,
        ),
      { loading: () => <StaticPageLoading /> },
    ),
  },
};

/**
 * ============================================================================
 *  SLUG LOCALIZATION — resolving translated URLs to registry entries
 * ============================================================================
 * The registry object key is the canonical English slug. Localized slugs live in
 * the dependency-free ./localizedSlugs map (single source of truth, also imported
 * by the core Link component). These helpers translate between a URL slug (as it
 * appears in the address bar for a given language) and the registry key.
 */

/** The URL slug a registry entry is served at in the given language (English key is the fallback). */
export const localizedStaticSlug = localizedSlug;

/**
 * Resolve a requested (lang, urlSlug) to the registry key, or undefined if it is
 * not a static page in that language. The English key is accepted as a fallback
 * only when the entry has no localized slug for that language — so once a page
 * has an Italian slug, its English slug no longer resolves under /it (letting the
 * caller 301 to the canonical localized URL instead of serving duplicate content).
 */
export const findStaticPageKey = (lang: string, urlSlug: string): string | undefined => {
  const hit = keyForLocalizedSlug(lang, urlSlug);
  if (hit) return hit;
  if (urlSlug in staticPages && !hasLocalizedSlug(urlSlug, lang)) return urlSlug;
  return undefined;
};

/** hreflang alternates for a registry entry: one localized URL per supported language. */
export const staticPageAlternates = (key: string): Alternative[] =>
  locales.map((lang) => ({
    key: lang,
    link: `${lang !== defaultLocale ? `/${lang}` : ''}/${localizedStaticSlug(key, lang)}`,
  }));
